<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import axios from '../../axios.js'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:isOpen'])

// Search state
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)
const searchError = ref(null)

// Selected player and aliases
const selectedPlayer = ref(null)
const selectedSource = ref(null) // 'player' or specific alias ID
const playerAliases = ref([])
const relatedGames = ref([])
const loadingGames = ref(false)
const loadingAliases = ref(false)
const gamesError = ref(null)

// Comparable data (default view)
const comparableData = ref([])
const loadingComparableData = ref(false)
const comparableDataError = ref(null)
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 100,
  total: 0
})

// View mode: 'comparable' (default) or 'player-games'
const viewMode = ref('comparable')

// Selection and editing state
const selectedRows = ref(new Set())
const editingRow = ref(null)
const editingValues = ref({})
const isSelectAll = ref(false)

// Multi-player selection shortcuts
const selectedPlayers = ref([])
const isMultiSelectMode = ref(false)
const playerAliasesMap = ref({})

// Search players using real API
const searchPlayers = async () => {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchResults.value = []
    return
  }
  
  isSearching.value = true
  searchError.value = null
  
  try {
    const response = await axios.get(`/api/players/search/${searchQuery.value}`)
    searchResults.value = response.data
  } catch (error) {
    console.error('Error searching players:', error)
    searchError.value = error.response?.data?.message || 'Failed to search players'
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}

// Load player aliases using real API
const loadPlayerAliases = async (playerId) => {
  // If we already have aliases for this player, don't reload
  if (playerAliasesMap.value[playerId]) {
    playerAliases.value = playerAliasesMap.value[playerId]
    return
  }
  
  loadingAliases.value = true
  
  try {
    const response = await axios.get(`/api/players/aliases/${playerId}`)
    const playerData = response.data
    
    if (playerData) {
      selectedPlayer.value = playerData
      const aliases = playerData.aliases || []
      playerAliases.value = aliases
      playerAliasesMap.value[playerId] = aliases
    } else {
      playerAliases.value = []
      playerAliasesMap.value[playerId] = []
    }
  } catch (error) {
    console.error('Error loading aliases:', error)
    playerAliases.value = []
    playerAliasesMap.value[playerId] = []
  } finally {
    loadingAliases.value = false
  }
}

// Load comparable data (boxscores) - default view
const loadComparableData = async (page = 1) => {
  loadingComparableData.value = true
  comparableDataError.value = null
  
  try {
    const params = {
      page,
      per_page: pagination.value.per_page
    }
    
    // Filter by player_id(s) based on selection mode
    if (selectedPlayers.value.length > 0) {
      if (isMultiSelectMode.value) {
        // Multi-selection mode: filter by all selected player IDs
        const playerIds = selectedPlayers.value.map(p => p.player_id || p.id)
        params.player_ids = playerIds
      } else {
        // Single selection mode: filter by single player ID
        params.player_id = selectedPlayer.value.player_id || selectedPlayer.value.id
      }
    }
    
    const response = await axios.get('/api/boxscores-management/comparable-data', { params })
    comparableData.value = response.data.data || []
    pagination.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total
    }
  } catch (error) {
    console.error('Error loading comparable data:', error)
    comparableDataError.value = error.response?.data?.message || 'Failed to load comparable data'
    comparableData.value = []
  } finally {
    loadingComparableData.value = false
  }
}

// Handle page change for comparable data
const handlePageChange = (page) => {
  loadComparableData(page)
}

// Select player and load their aliases
const selectPlayer = async (player) => {
  selectedPlayer.value = player
  searchQuery.value = ''
  searchResults.value = []
  
  // If player already has aliases from search, use them
  if (player.aliases && Array.isArray(player.aliases)) {
    playerAliases.value = player.aliases
  } else {
    // Otherwise fetch them
    await loadPlayerAliases(player.player_id || player.id)
  }
  
  // Auto-select player as source and load games
  selectedSource.value = 'player'
  viewMode.value = 'player-games'
  loadGames()
}

// Select source (player or alias)
const selectSource = (source) => {
  selectedSource.value = source
  loadGames()
}

// Check if source is selected
const isSourceSelected = (source) => {
  return selectedSource.value === source
}

// Get selected source display name
const selectedSourceName = computed(() => {
  if (!selectedPlayer.value) return ''
  if (selectedSource.value === 'player') {
    return selectedPlayer.value.player_name
  }
  const alias = playerAliases.value.find(a => a.id === selectedSource.value)
  return alias ? alias.player_name : ''
})

// Get selected source ID for API call
const selectedSourceId = computed(() => {
  if (!selectedPlayer.value || !selectedSource.value) return null
  if (selectedSource.value === 'player') {
    return selectedPlayer.value.player_id || selectedPlayer.value.id
  }
  return selectedSource.value // This is the alias ID
})

// Load games for selected source from comparable_data
const loadGames = async () => {
  if (!selectedSource.value || !selectedPlayer.value) return
  
  loadingGames.value = true
  gamesError.value = null
  
  try {
    let playerId = null
    
    // If player is selected, use their player_id
    if (selectedSource.value === 'player') {
      playerId = selectedPlayer.value.player_id || selectedPlayer.value.id
    } else {
      // If alias is selected, use the alias ID as the player_id to search in comparable_data
      playerId = selectedSource.value // The alias ID is used as player_id in comparable_data
    }
    
    if (!playerId) {
      throw new Error('No player ID found')
    }
    
    // Fetch games from comparable_data based on player_id
    const response = await axios.get('/api/boxscores-management/player-games', {
      params: {
        player_id: playerId,
        per_page: 100
      }
    })
    
    relatedGames.value = response.data.data || response.data.games || response.data || []
  } catch (error) {
    console.error('Error loading games:', error)
    gamesError.value = error.response?.data?.message || 'Failed to load games'
    relatedGames.value = []
  } finally {
    loadingGames.value = false
  }
}

// Clear selection and return to comparable data view
const clearSelection = () => {
  selectedPlayer.value = null
  playerAliases.value = []
  relatedGames.value = []
  selectedSource.value = null
  searchError.value = null
  gamesError.value = null
  viewMode.value = 'comparable'
}

// Row selection functions
const toggleRowSelection = (rowId) => {
  if (selectedRows.value.has(rowId)) {
    selectedRows.value.delete(rowId)
  } else {
    selectedRows.value.add(rowId)
  }
  updateSelectAllState()
}

const toggleSelectAll = () => {
  if (isSelectAll.value) {
    selectedRows.value.clear()
  } else {
    comparableData.value.forEach(item => selectedRows.value.add(item.id))
  }
  isSelectAll.value = !isSelectAll.value
}

const updateSelectAllState = () => {
  isSelectAll.value = selectedRows.value.size === comparableData.value.length && comparableData.value.length > 0
}

// Edit functions
const startEditRow = (row) => {
  editingRow.value = row.id
  editingValues.value = {
    player_name: row.player_name,
    team_name: row.team_name || row.team,
    opponent_team: row.opponent_team,
    points: row.points,
    assists: row.assists,
    total_rebounds: row.total_rebounds,
    blocks: row.blocks,
    steals: row.steals,
    turnovers: row.turnovers,
    three_point_field_goal: row.three_point_field_goal,
    minutes: row.minutes,
  }
}

const cancelEditRow = () => {
  editingRow.value = null
  editingValues.value = {}
}

const saveEditRow = async (rowId) => {
  try {
    // Here you would make an API call to update the row
    console.log('Saving row:', rowId, editingValues.value)
    
    // Update local data
    const rowIndex = comparableData.value.findIndex(item => item.id === rowId)
    if (rowIndex !== -1) {
      Object.assign(comparableData.value[rowIndex], editingValues.value)
    }
    
    editingRow.value = null
    editingValues.value = {}
  } catch (error) {
    console.error('Error saving row:', error)
  }
}

// Multi-player selection functions
const togglePlayerSelection = (player) => {
  if (!isMultiSelectMode.value) {
    // Single selection mode - replace current selection
    selectedPlayers.value = [player]
    selectedPlayer.value = player
    playerAliases.value = []
    loadPlayerAliases(player.player_id || player.id)
    selectPlayer(player)
    // Reload comparable data to filter by selected player
    loadComparableData(1)
  } else {
    // Multi selection mode - add/remove from list
    const existingIndex = selectedPlayers.value.findIndex(p => 
      (p.player_id || p.id) === (player.player_id || player.id)
    )
    
    if (existingIndex > -1) {
      selectedPlayers.value.splice(existingIndex, 1)
    } else {
      selectedPlayers.value.push(player)
      // Load aliases for the newly added player
      loadPlayerAliases(player.player_id || player.id)
    }
    // Reload comparable data to filter by all selected players
    loadComparableData(1)
  }
}

const isPlayerSelected = (player) => {
  return selectedPlayers.value.some(p => 
    (p.player_id || p.id) === (player.player_id || player.id)
  )
}

const toggleMultiSelectMode = () => {
  isMultiSelectMode.value = !isMultiSelectMode.value
  if (!isMultiSelectMode.value) {
    // Clear multi-selection when exiting multi-select mode
    if (selectedPlayers.value.length > 0) {
      selectedPlayer.value = selectedPlayers.value[0]
      selectedPlayers.value = [selectedPlayer.value]
      // Reload comparable data to filter by selected player
      loadComparableData(1)
    }
  } else {
    // When entering multi-select mode, reload all data (no filter)
    loadComparableData(1)
  }
}

const clearAllSelections = () => {
  selectedPlayers.value = []
  selectedPlayer.value = null
  playerAliases.value = []
  playerAliasesMap.value = {}
  relatedGames.value = []
  selectedSource.value = null
  viewMode.value = 'comparable'
  // Reload comparable data without filters
  loadComparableData(1)
}

const removePlayerFromSelection = (playerToRemove) => {
  const index = selectedPlayers.value.findIndex(p => 
    (p.player_id || p.id) === (playerToRemove.player_id || playerToRemove.id)
  )
  if (index > -1) {
    selectedPlayers.value.splice(index, 1)
    
    // Clean up aliases map for removed player
    const playerId = playerToRemove.player_id || playerToRemove.id
    if (playerAliasesMap.value[playerId]) {
      delete playerAliasesMap.value[playerId]
    }
    
    // If this was the active player, switch to the first remaining player
    if (selectedPlayer.value && (selectedPlayer.value.player_id || selectedPlayer.value.id) === (playerToRemove.player_id || playerToRemove.id)) {
      if (selectedPlayers.value.length > 0) {
        selectedPlayer.value = selectedPlayers.value[0]
        loadPlayerAliases(selectedPlayer.value.player_id || selectedPlayer.value.id)
      } else {
        selectedPlayer.value = null
        playerAliases.value = []
        selectedSource.value = null
        viewMode.value = 'comparable'
      }
    }
    
    // Reload comparable data to reflect the updated selection
    loadComparableData(1)
  }
}

// Close overlay
const closeOverlay = () => {
  clearSelection()
  searchQuery.value = ''
  searchResults.value = []
  emit('close')
  emit('update:isOpen', false)
}

// Get game count for source (computed from actual game counts)
const getGameCount = (source) => {
  if (source === 'player' && selectedPlayer.value) {
    return selectedPlayer.value.games_count || 0
  }
  const alias = playerAliases.value.find(a => a.id === source)
  return alias?.games_count || 0
}

// Load comparable data when overlay opens
watch(() => props.isOpen, (newVal) => {
  if (newVal && comparableData.value.length === 0) {
    loadComparableData(1)
  }
})

// Also load on mount if already open
onMounted(() => {
  if (props.isOpen) {
    loadComparableData(1)
  }
})
</script>

<template>
  <!-- Fullscreen Overlay -->
  <Transition name="overlay">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-2"
      @click.self="closeOverlay"
    >
      <div class="relative w-[98vw] h-[96vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i class="fas fa-diagram-project text-white text-xl"></i>
            </div>
            <div>
              <h2 class="text-xl font-bold text-white">Player-Alias Game Relations</h2>
              <p class="text-blue-100 text-sm">View player aliases and their related games</p>
            </div>
          </div>
          <button
            @click="closeOverlay"
            class="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <!-- Main Content -->
        <div class="flex-1 overflow-hidden flex">
          <!-- Left Panel - Player Search & Selection -->
          <div class="w-[400px] border-r border-gray-200 flex flex-col bg-gray-50">
            <!-- Search Box -->
            <div class="p-4 border-b border-gray-200 bg-white">
              <div class="relative">
                <input 
                  v-model="searchQuery"
                  @input="searchPlayers"
                  type="text" 
                  placeholder="Search player by name..."
                  class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                
                <!-- Search indicator -->
                <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <i class="fas fa-spinner fa-spin text-purple-500"></i>
                </div>

                <!-- Multi-select toggle -->
                <div class="flex items-center justify-between mt-3">
                  <button
                    @click="toggleMultiSelectMode"
                    :class="[
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isMultiSelectMode 
                        ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    ]"
                  >
                    <i :class="isMultiSelectMode ? 'fas fa-check-square' : 'fas fa-square'" class="text-xs"></i>
                    {{ isMultiSelectMode ? 'Multi-Select Mode' : 'Single Select' }}
                  </button>
                  
                  <button
                    v-if="selectedPlayers.length > 0"
                    @click="clearAllSelections"
                    class="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Clear All
                  </button>
                </div>

                <!-- Search Results Dropdown -->
                <div 
                  v-if="searchResults.length > 0"
                  class="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-10"
                >
                  <div 
                    v-for="player in searchResults" 
                    :key="player.player_id || player.id"
                    @click="togglePlayerSelection(player)"
                    :class="[
                      'px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors',
                      isPlayerSelected(player) ? 'bg-purple-50' : 'hover:bg-purple-50'
                    ]"
                  >
                    <div class="flex items-center gap-3">
                      <div class="flex items-center gap-2">
                        <div 
                          v-if="isMultiSelectMode"
                          class="w-4 h-4 rounded border-2 flex items-center justify-center"
                          :class="isPlayerSelected(player) ? 'bg-purple-600 border-purple-600' : 'border-gray-300'"
                        >
                          <i v-if="isPlayerSelected(player)" class="fas fa-check text-white text-xs"></i>
                        </div>
                        <div 
                          class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          :class="player.match_type === 'alias' ? 'bg-purple-100' : 'bg-blue-100'"
                        >
                          <i 
                            class="text-sm"
                            :class="player.match_type === 'alias' ? 'fas fa-user-tag text-purple-600' : 'fas fa-user text-blue-600'"
                          ></i>
                        </div>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <span class="font-medium text-gray-900 truncate">{{ player.player_name }}</span>
                        </div>
                        <div class="flex items-center gap-2 flex-wrap">
                          <span class="text-xs text-gray-500">ID: {{ player.player_id || player.id }}</span>
                          
                          <!-- Match Type Badge -->
                          <span 
                            v-if="player.match_type === 'alias'" 
                            class="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium"
                            :title="`Matched via alias: ${player.matched_name}`"
                          >
                            <i class="fas fa-user-tag mr-1"></i>Alias Match
                          </span>
                          <span 
                            v-else
                            class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium"
                          >
                            <i class="fas fa-user mr-1"></i>Direct Match
                          </span>
                          
                          <!-- Has Aliases Badge -->
                          <span 
                            v-if="player.aliases && player.aliases.length > 0" 
                            class="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium"
                            :title="`This player has ${player.aliases.length} alias${player.aliases.length !== 1 ? 'es' : ''}`"
                          >
                            <i class="fas fa-list mr-1"></i>{{ player.aliases.length }} alias{{ player.aliases.length !== 1 ? 'es' : '' }}
                          </span>
                        </div>
                        <div v-if="player.match_type === 'alias' && player.matched_name" class="text-xs text-purple-600 mt-1 italic">
                          Matched: "{{ player.matched_name }}"
                        </div>
                      </div>
                      <i class="fas fa-chevron-right text-gray-400 text-xs flex-shrink-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Error -->
              <div v-if="searchError" class="mt-2 text-red-500 text-sm flex items-center gap-2">
                <i class="fas fa-exclamation-circle"></i>
                {{ searchError }}
              </div>
            </div>

            <!-- Selected Players Info -->
            <div v-if="selectedPlayers.length > 0" class="p-4 bg-white border-b border-gray-200">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-gray-500 uppercase">
                    {{ selectedPlayers.length > 1 ? 'Selected Players' : 'Selected Player' }}
                  </span>
                  <span class="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                    {{ selectedPlayers.length }}
                  </span>
                </div>
                <button 
                  @click="clearAllSelections"
                  class="text-gray-400 hover:text-red-500 text-xs"
                >
                  <i class="fas fa-times mr-1"></i>Clear All
                </button>
              </div>
              
              <!-- Selected Players List -->
              <div class="space-y-2">
                <div 
                  v-for="(player, index) in selectedPlayers"
                  :key="player.player_id || player.id"
                  class="bg-purple-50 rounded-lg p-3 border border-purple-200"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                      <i class="fas fa-user text-white text-sm"></i>
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold text-gray-900">{{ player.player_name }}</div>
                      <div class="text-xs text-gray-600">Player ID: {{ player.player_id || player.id }}</div>
                      <div class="text-xs text-gray-600 mt-1 flex items-center gap-3">
                        <span v-if="player.games_count !== undefined" class="text-green-600">
                          <i class="fas fa-basketball-ball mr-1"></i>{{ player.games_count }} game{{ player.games_count !== 1 ? 's' : '' }}
                        </span>
                        <span v-if="player.team">
                          <i class="fas fa-shield-alt mr-1"></i>{{ player.team }}
                        </span>
                      </div>
                    </div>
                    <button
                      v-if="selectedPlayers.length > 1"
                      @click="removePlayerFromSelection(player)"
                      class="text-gray-400 hover:text-red-500 p-1"
                    >
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  
                  <!-- Aliases for this player (hierarchical menu style) -->
                  <div class="mt-3 ml-4 border-l-2 border-purple-200 pl-3">
                    <div class="text-xs font-medium text-gray-500 mb-2">Aliases</div>
                    <div class="space-y-1">
                      <div 
                        v-for="alias in (playerAliasesMap[player.player_id || player.id] || [])"
                        :key="alias.id"
                        @click="selectSource(alias.id)"
                        :class="[
                          'flex items-center gap-2 p-2 rounded cursor-pointer transition-colors',
                          selectedSource === alias.id 
                            ? 'bg-blue-100 border border-blue-200' 
                            : 'hover:bg-purple-100 border border-transparent'
                        ]"
                      >
                        <div class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                          <i class="fas fa-user-tag text-purple-600 text-xs"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="font-medium text-sm text-gray-900 truncate">{{ alias.player_name }}</div>
                          <div class="text-xs text-gray-500">
                            Alias ID: {{ alias.id }} • Player ID: {{ alias.player_id }}
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <span class="text-xs text-gray-500">{{ alias.games_count || 0 }} games</span>
                          <i v-if="selectedSource === alias.id" class="fas fa-check-circle text-blue-600 text-sm"></i>
                        </div>
                      </div>
                      <div v-if="!playerAliasesMap[player.player_id || player.id] || playerAliasesMap[player.player_id || player.id].length === 0" class="text-xs text-gray-400 italic py-2">
                        No aliases found
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Player/Alias Source Selection -->
            <div v-if="selectedPlayer" class="flex-1 overflow-y-auto p-4">
              <!-- Loading aliases -->
              <div v-if="loadingAliases" class="text-center py-8">
                <i class="fas fa-spinner fa-spin text-purple-500 text-2xl mb-2"></i>
                <p class="text-sm text-gray-600">Loading aliases...</p>
              </div>

              <!-- Aliases content -->
              <div v-else>
                <h3 class="text-sm font-semibold text-gray-700 mb-3 uppercase">Select Source</h3>
                
                <!-- Main Player Source -->
                <div 
                  @click="selectSource('player')"
                  :class="[
                    'mb-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    isSourceSelected('player') 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow'
                  ]"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <i class="fas fa-user text-white text-sm"></i>
                      </div>
                      <div>
                        <div class="font-semibold text-gray-900">{{ selectedPlayer.player_name }}</div>
                        <div class="text-xs text-gray-500">
                          <i class="fas fa-hashtag"></i> {{ selectedPlayer.player_id || selectedPlayer.id }}
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                        {{ getGameCount('player') }} games
                      </span>
                      <i v-if="isSourceSelected('player')" class="fas fa-check-circle text-blue-500"></i>
                    </div>
                  </div>
                </div>

                <!-- Aliases List -->
                <div v-if="playerAliases.length > 0" class="space-y-2">
                  <h4 class="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Aliases ({{ playerAliases.length }})</h4>
                  <div 
                    v-for="alias in playerAliases"
                    :key="alias.id"
                    @click="selectSource(alias.id)"
                    :class="[
                      'p-3 rounded-lg border-2 cursor-pointer transition-all',
                      isSourceSelected(alias.id) 
                        ? 'border-purple-500 bg-purple-50 shadow-md' 
                        : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow'
                    ]"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2">
                        <div class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                          <i class="fas fa-user-tag text-white text-sm"></i>
                        </div>
                        <div>
                          <div class="font-semibold text-gray-900">{{ alias.player_name }}</div>
                          <div class="text-xs text-gray-500">
                            <i class="fas fa-link"></i> Alias ID: {{ alias.id }}
                            <i class="fas fa-hashtag"></i> Player ID: {{ alias.player_id }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <span class="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                          {{ getGameCount(alias.id) }} games
                        </span>
                        <i v-if="isSourceSelected(alias.id)" class="fas fa-check-circle text-purple-500"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No aliases message -->
                <div v-else class="text-center py-8 text-gray-400">
                  <i class="fas fa-inbox text-3xl mb-2"></i>
                  <p class="text-sm">No aliases found</p>
                </div>
              </div>
            </div>

            <!-- Instructions when no player selected -->
            <div v-else class="flex-1 flex items-center justify-center p-6">
              <div class="text-center text-gray-400">
                <i class="fas fa-search text-4xl mb-3"></i>
                <p class="text-sm">Search for a player to view relations</p>
              </div>
            </div>
          </div>

          <!-- Right Panel - Content -->
          <div class="flex-1 flex flex-col overflow-hidden bg-gray-50">
            <!-- Header for Player Games Mode -->
            <div v-if="viewMode === 'player-games' && selectedPlayer && selectedSource" class="px-6 py-4 border-b border-gray-200 bg-white">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Related Games</h3>
                  <p class="text-sm text-gray-600">
                    Showing games for: 
                    <span class="font-semibold text-purple-600">{{ selectedSourceName }}</span>
                    <span v-if="selectedSource === 'player'" class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      Player ID: {{ selectedPlayer.player_id }}
                    </span>
                    <span v-else class="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                      Alias ID: {{ selectedSource }}
                    </span>
                  </p>
                </div>
                <div class="text-sm font-medium text-gray-700">
                  Total: <span class="text-purple-600 text-lg">{{ relatedGames.length }}</span> games
                </div>
              </div>
            </div>

            <!-- Header for Comparable Data Mode -->
            <div v-if="viewMode === 'comparable'" class="px-6 py-4 border-b border-gray-200 bg-white">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Comparable Data Management</h3>
                  <p class="text-sm text-gray-600">Select rows to manage player boxscore data</p>
                </div>
                <div class="flex items-center gap-4">
                  <div class="text-sm font-medium text-gray-700">
                    Selected: <span class="text-purple-600 text-lg">{{ selectedRows.size }}</span> / {{ pagination.total }}
                  </div>
                  <div class="text-sm font-medium text-gray-700">
                    Total: <span class="text-purple-600 text-lg">{{ pagination.total }}</span> records
                    <span class="text-gray-500 ml-2">(Page {{ pagination.current_page }} of {{ pagination.last_page }})</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Content Area -->
            <div class="flex-1 overflow-y-auto p-6">
              <!-- COMPARABLE DATA VIEW -->
              <div v-if="viewMode === 'comparable'">
                <!-- Loading state -->
                <div v-if="loadingComparableData" class="flex items-center justify-center py-12">
                  <div class="text-center">
                    <i class="fas fa-spinner fa-spin text-purple-500 text-3xl mb-3"></i>
                    <p class="text-gray-600">Loading comparable data...</p>
                  </div>
                </div>

                <!-- Error state -->
                <div v-if="!loadingComparableData && comparableDataError" class="flex items-center justify-center py-12">
                  <div class="text-center text-red-500">
                    <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
                    <p class="mb-3">{{ comparableDataError }}</p>
                    <button 
                      @click="loadComparableData(pagination.current_page)"
                      class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <i class="fas fa-redo mr-2"></i>Retry
                    </button>
                  </div>
                </div>

                <!-- Comparable data content -->
                <div v-if="!loadingComparableData && !comparableDataError" class="space-y-3">
                  <!-- No data message -->
                  <div v-if="comparableData.length === 0" class="text-center py-12">
                    <i class="fas fa-database text-gray-300 text-4xl mb-3"></i>
                    <p class="text-gray-600">No comparable data found</p>
                  </div>

                  <!-- Data table -->
                  <div v-if="!loadingComparableData && !comparableDataError && comparableData.length > 0">
                    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50">
                          <tr>
                            <th class="px-4 py-3 text-left">
                              <input 
                                type="checkbox" 
                                :checked="isSelectAll"
                                @change="toggleSelectAll"
                                class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              >
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aliases</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opponent</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">League</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Season</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PTS</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AST</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REB</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BLK</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STL</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TOV</th>
                            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">3PT</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                          <tr 
                            v-for="item in comparableData"
                            :key="item.id"
                            :class="[
                              'hover:bg-gray-50 transition-colors',
                              selectedRows.has(item.id) ? 'bg-blue-50' : ''
                            ]"
                          >
                            <!-- Checkbox -->
                            <td class="px-4 py-3">
                              <input 
                                type="checkbox" 
                                :checked="selectedRows.has(item.id)"
                                @change="toggleRowSelection(item.id)"
                                class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              >
                            </td>
                            
                            <!-- ID -->
                            <td class="px-4 py-3 text-sm">
                              <span class="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                                {{ item.player_id }}
                              </span>
                            </td>
                            
                            <!-- Player -->
                            <td class="px-4 py-3 text-sm">
                              <span class="font-medium text-gray-900">{{ item.player_name }}</span>
                            </td>
                            
                            <!-- Aliases -->
                            <td class="px-4 py-3 text-sm">
                              <div class="flex flex-wrap gap-1">
                                <span 
                                  v-for="alias in item.aliases" 
                                  :key="alias.id"
                                  class="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-200"
                                  :title="`Alias ID: ${alias.id}`"
                                >
                                  {{ alias.id }}
                                </span>
                                <span v-if="!item.aliases || item.aliases.length === 0" class="text-gray-400 text-xs italic">
                                  No aliases
                                </span>
                              </div>
                            </td>
                            
                            <!-- Team -->
                            <td class="px-4 py-3 text-sm text-gray-900">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.team_name"
                                  type="text" 
                                  class="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else>{{ item.team_name || item.team }}</span>
                            </td>
                            
                            <!-- Opponent -->
                            <td class="px-4 py-3 text-sm text-gray-900">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.opponent_team"
                                  type="text" 
                                  class="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else>{{ item.opponent_team || 'N/A' }}</span>
                            </td>
                            
                            <!-- League -->
                            <td class="px-4 py-3 text-sm text-gray-900">
                              <span class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                {{ item.league_name }}
                              </span>
                            </td>
                            
                            <!-- Date -->
                            <td class="px-4 py-3 text-sm text-gray-900">
                              {{ new Date(item.game_date).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric'
                              }) }}
                            </td>
                            
                            <!-- Season -->
                            <td class="px-4 py-3 text-sm text-gray-900">{{ item.season || 'N/A' }}</td>
                            
                            <!-- Minutes -->
                            <td class="px-4 py-3 text-sm text-gray-900">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.minutes"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else>{{ item.minutes || 0 }}</span>
                            </td>
                            
                            <!-- Points -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.points"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.points !== null" class="font-medium text-purple-600">{{ item.points }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Assists -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.assists"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.assists !== null" class="font-medium text-blue-600">{{ item.assists }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Rebounds -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.total_rebounds"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.total_rebounds !== null" class="font-medium text-green-600">{{ item.total_rebounds }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Blocks -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.blocks"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.blocks !== null" class="font-medium text-orange-600">{{ item.blocks }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Steals -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.steals"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.steals !== null" class="font-medium text-red-600">{{ item.steals }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Turnovers -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.turnovers"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.turnovers !== null" class="font-medium text-yellow-600">{{ item.turnovers }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- 3PT -->
                            <td class="px-4 py-3 text-sm">
                              <span v-if="editingRow === item.id">
                                <input 
                                  v-model="editingValues.three_point_field_goal"
                                  type="number" 
                                  step="0.1"
                                  class="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                              </span>
                              <span v-else-if="item.three_point_field_goal !== null" class="font-medium text-indigo-600">{{ item.three_point_field_goal }}</span>
                              <span v-else class="text-gray-400">-</span>
                            </td>
                            
                            <!-- Actions -->
                            <td class="px-4 py-3 text-center">
                              <div class="flex justify-center gap-1">
                                <button 
                                  v-if="editingRow !== item.id"
                                  @click="startEditRow(item)"
                                  class="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                  title="Edit"
                                >
                                  <i class="fas fa-edit text-xs"></i>
                                </button>
                                <button 
                                  v-if="editingRow === item.id"
                                  @click="saveEditRow(item.id)"
                                  class="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                                  title="Save"
                                >
                                  <i class="fas fa-check text-xs"></i>
                                </button>
                                <button 
                                  v-if="editingRow === item.id"
                                  @click="cancelEditRow"
                                  class="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                  title="Cancel"
                                >
                                  <i class="fas fa-times text-xs"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Pagination -->
                    <div v-if="pagination.last_page > 1" class="mt-6 flex items-center justify-center gap-2">
                      <button 
                        @click="handlePageChange(pagination.current_page - 1)"
                        :disabled="pagination.current_page === 1"
                        class="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <i class="fas fa-chevron-left"></i>
                      </button>
                      
                      <span class="px-4 py-2 text-sm text-gray-600">
                        Page {{ pagination.current_page }} of {{ pagination.last_page }}
                      </span>
                      
                      <button 
                        @click="handlePageChange(pagination.current_page + 1)"
                        :disabled="pagination.current_page === pagination.last_page"
                        class="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              <!-- PLAYER GAMES VIEW -->
              <div v-if="viewMode === 'player-games'">
                <!-- Loading state -->
                <div v-if="loadingGames" class="flex items-center justify-center py-12">
                  <div class="text-center">
                    <i class="fas fa-spinner fa-spin text-purple-500 text-3xl mb-3"></i>
                    <p class="text-gray-600">Loading games...</p>
                  </div>
                </div>

                <!-- Error state -->
                <div v-if="!loadingGames && gamesError" class="flex items-center justify-center py-12">
                  <div class="text-center text-red-500">
                    <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
                    <p class="mb-3">{{ gamesError }}</p>
                    <button 
                      @click="loadGames"
                      class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <i class="fas fa-redo mr-2"></i>Retry
                    </button>
                  </div>
                </div>

                <!-- Games content -->
                <div v-if="!loadingGames && !gamesError && selectedPlayer && selectedSource" class="space-y-3">
                <!-- No games message -->
                <div v-if="relatedGames.length === 0" class="text-center py-12">
                  <i class="fas fa-basketball-ball text-gray-300 text-4xl mb-3"></i>
                  <p class="text-gray-600">No games found for this source</p>
                </div>

                <!-- Games table -->
                <div v-else class="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opponent</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">League</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Season</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PTS</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AST</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REB</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BLK</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STL</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TOV</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">3PT</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr 
                          v-for="game in relatedGames"
                          :key="game.id"
                          class="hover:bg-gray-50 transition-colors"
                        >
                          <!-- ID -->
                          <td class="px-4 py-3 text-sm">
                            <span class="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                              {{ game.player_id }}
                            </span>
                          </td>
                          
                          <!-- Player -->
                          <td class="px-4 py-3 text-sm">
                            <span class="font-medium text-gray-900">{{ game.player_name }}</span>
                          </td>
                          
                          <!-- Team -->
                          <td class="px-4 py-3 text-sm text-gray-900">
                            {{ game.team_name || game.team }}
                          </td>
                          
                          <!-- Opponent -->
                          <td class="px-4 py-3 text-sm text-gray-900">
                            {{ game.opponent_team || 'N/A' }}
                          </td>
                          
                          <!-- League -->
                          <td class="px-4 py-3 text-sm text-gray-900">
                            <span class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {{ game.league_name }}
                            </span>
                          </td>
                          
                          <!-- Date -->
                          <td class="px-4 py-3 text-sm text-gray-900">
                            {{ new Date(game.game_date).toLocaleDateString('en-GB', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric'
                            }) }}
                          </td>
                          
                          <!-- Season -->
                          <td class="px-4 py-3 text-sm text-gray-900">{{ game.season || 'N/A' }}</td>
                          
                          <!-- Minutes -->
                          <td class="px-4 py-3 text-sm text-gray-900">{{ game.minutes || 0 }}</td>
                          
                          <!-- Points -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.points !== null" class="font-medium text-purple-600">{{ game.points }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- Assists -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.assists !== null" class="font-medium text-blue-600">{{ game.assists }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- Rebounds -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.total_rebounds !== null" class="font-medium text-green-600">{{ game.total_rebounds }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- Blocks -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.blocks !== null" class="font-medium text-orange-600">{{ game.blocks }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- Steals -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.steals !== null" class="font-medium text-red-600">{{ game.steals }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- Turnovers -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.turnovers !== null" class="font-medium text-yellow-600">{{ game.turnovers }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          
                          <!-- 3PT -->
                          <td class="px-4 py-3 text-sm">
                            <span v-if="game.three_point_field_goal !== null" class="font-medium text-indigo-600">{{ game.three_point_field_goal }}</span>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- No selection message for player games mode -->
                <div v-if="!loadingGames && !gamesError && (!selectedPlayer || !selectedSource)" class="flex items-center justify-center py-12">
                  <div class="text-center text-gray-400">
                    <i class="fas fa-basketball-ball text-5xl mb-4"></i>
                    <p class="text-lg font-medium text-gray-600 mb-2">No Source Selected</p>
                    <p class="text-sm">Select a player or alias to view related games</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-active > div,
.overlay-leave-active > div {
  transition: transform 0.3s ease;
}

.overlay-enter-from > div,
.overlay-leave-to > div {
  transform: scale(0.95);
}

/* Custom scrollbar */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>


